import React, { Component } from "react";
import { Link } from "@material-ui/core";
import styled from "styled-components";

import Icon from "js/react/components/Icon";
import { getStaticUrl } from "js/config";
import { Gap, Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { BlueButton } from "js/react/components/UiComponents";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { app } from "js/namespaces";
import { FeatureType } from "common/features";
import { UIController } from "js/editor/dialogs/UIController";

const Pane = styled.div`
  display:flex;
  padding: 30px 50px 30px  40px;
  background: white;
  border-radius: 4px;
  max-width: 820px;
  letter-spacing: 0px !important;
  div {
    h1 {
      text-transform: none;
      font-size: 22px;
      margin: 0;
      letter-spacing: 0px;
    }
    h4 {
      text-transform: none;
      font-size: 18px;
      margin: 0;
    }
    p:first-of-type {
      margin-top: 10px;
    }
    p, a {
      font-size: 15px;
      opacity: 0.8;
      letter-spacing: 0px;
    }
    p + p {
      margin-top: 10px;
    }
  }

  .dark-mode & {
    background: #111;
  }
`;

const CheckPoint = styled.div`
  display: flex;
  font-size: 14px;
  margin-bottom: 20px;
  i {
    color: #11A9E2 !important;
    margin-right: 15px;
  }
`;

export class NoResourcesNotices extends Component {
    render() {
        return (
            <FlexBox fill>
                <Pane>
                    <Icon style={{ color: "#FFAA00", marginTop: 3, fontSize: 24 }} iconName="new_releases"/>
                    <Gap10/>
                    <div>
                        {this.props.children}
                    </div>
                </Pane>
            </FlexBox>
        );
    }
}

export class LibrarianNoSharedSlidesNotice extends Component {
    render() {
        return (
            <FlexBox fill>
                <Pane>
                    <Icon style={{ color: "#FFAA00", marginTop: 4, fontSize: 22 }} iconName="new_releases"/>
                    <Gap20/>
                    <div>
                        <div>
                            <h1>You haven't added any team slides yet</h1>
                            <Gap10/>
                            <p>Keep your team slides current and consistent by creating reusable slides for your team
                                members. When you update team slides, the changes are applied automatically across
                                all presentations where they are used.</p>
                        </div>
                        <Gap size={40}/>
                        <FlexBox verticalAlign="top">
                            <img style={{ width: 275 }} src={getStaticUrl("/images/add_shared_slide.gif")}/>
                            <Gap size={60}/>
                            <div>
                                <h4>Add slides to your shared team library from a presentation</h4>
                                <Gap30/>
                                <CheckPoint>
                                    <Icon iconName="check"/>
                                    Open a presentation
                                </CheckPoint>
                                <CheckPoint>
                                    <Icon iconName="check"/>
                                    Find the slide you'd like to add or create a new one
                                </CheckPoint>
                                <CheckPoint>
                                    <Icon iconName="check"/>
                                    Choose "Add to Team Slides" from the slide menu
                                </CheckPoint>
                                <Gap20/>
                                <a href="https://beautifulai.zendesk.com/hc/en-us/articles/360047188952" target="_blank">Learn more...</a>
                            </div>
                        </FlexBox>
                    </div>
                </Pane>
            </FlexBox>
        );
    }
}

export class MemberNoSharedSlidesNotice extends Component {
    render() {
        return (
            <NoResourcesNotices>
                <div>
                    <h1>No slides have been added to your shared team library yet</h1>
                    <p>Owners and Librarians in your team can add shared team slides that you can reuse to keep
                        everyone on brand and up-to-date.</p>
                    <p>When team slides are updated, the changes are automatically applied to all
                        presentations that use them.</p>
                    <Gap20/>
                    <a href="https://beautifulai.zendesk.com/hc/en-us/articles/360047188952" target="_blank">Learn more...</a>
                </div>
            </NoResourcesNotices>
        );
    }
}

export class UpgradeToTeamProNotice extends Component {
    handlePricingPage = () => {
        const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, this.props.workspaceId);
        const currentPlan = userIsBasicInWorkspace ? "basic" : "pro";
        window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
    }

    handleSalesCalendar = () => {
        window.open("https://calendly.com/beautifulteam/demo-call-with-beautiful-ai-upgrade-to-team", "_blank");
    }

    render() {
        return (
            <NoResourcesNotices>
                <div>
                    <h1>Upgrade to the Team plan and share slides with your team</h1>
                    <p>Keep your shared team slides current and consistent by creating reusable slides for your team.
                        When you update team slides, the changes are applied automatically across
                        all presentations where they are used.</p>
                    <Link underline="hover" onClick={this.handlePricingPage}>See our Plans</Link>
                    <Gap30/>
                    <BlueButton onClick={this.handleSalesCalendar}>Talk To An Expert</BlueButton>
                </div>
            </NoResourcesNotices>
        );
    }
}

export class NoTeamTemplateNotice extends Component {
    handlePricingPage = () => {
        const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, app.appState.workspaceId);
        const currentPlan = userIsBasicInWorkspace ? "basic" : "pro";
        window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
    }

    handleSalesCalendar = () => {
        window.open("https://calendly.com/beautifulteam/demo-call-with-beautiful-ai-upgrade-to-team", "_blank");
    }

    render() {
        let title;
        let isUpgrade = false;
        if (UIController.isTeamWorkspace()) {
            title = "No team templates have been added to your team yet.";
        } else {
            title = "Upgrade to the Team plan and share presentation templates with your team";
            isUpgrade = true;
        }

        return (
            <NoResourcesNotices>
                <div>
                    <h1>{title}</h1>
                    <p>Team Templates make it quick and easy for members of your team to get started with a new presentation by
                        starting from a pre-built template instead of a blank presentation. </p>
                    {isUpgrade && <>
                        <Link underline="hover" onClick={this.handlePricingPage}>See our Plans</Link>
                        <Gap30/>
                        <BlueButton onClick={this.handleSalesCalendar}>Talk To An Expert</BlueButton>
                    </>}
                </div>
            </NoResourcesNotices>
        );
    }
}

export class NoTeamFoldersNotice extends Component {
    render() {
        return (
            <NoResourcesNotices>
                <div>
                    <h1>You haven't added any Team Folders yet</h1>
                    <p>Team Folders make it easy for you to organize and share your presentations with all or some of your team members.</p>
                </div>
            </NoResourcesNotices>
        );
    }
}
